import React, { useState } from 'react';
import {
    AnalysisSettings,
    type AnalysisSettingsProps,
} from '@/features/NewSourceModal/components/AnalysisSettings';
import { TextField } from '@/components/TextField';
import { useSourceCreationStore } from '@/stores/source-creation.store';
import { getFileExtension, stripFileExtension } from '@/utils/file-name';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';

type UploadFileMetadataProps = Pick<
    AnalysisSettingsProps,
    | 'faceRecognition'
    | 'onFaceRecognitionChange'
    | 'licensePlateRecognition'
    | 'onLicensePlateRecognitionChange'
    | 'opticalCharacterRecognition'
    | 'onOpticalCharacterRecognitionChange'
    | 'analysisFramesPerSecond'
    | 'onAnalysisFramesPerSecondChange'
> & {
    fileName: string;
    onFileNameChange: (name: string) => void;
};

export function UploadFileMetadata({
    fileName,
    onFileNameChange,
    faceRecognition,
    onFaceRecognitionChange,
    licensePlateRecognition,
    onLicensePlateRecognitionChange,
    opticalCharacterRecognition,
    onOpticalCharacterRecognitionChange,
    analysisFramesPerSecond,
    onAnalysisFramesPerSecondChange,
}: UploadFileMetadataProps) {
    const isProcedure = useSourceCreationStore(
        (state) => state.targetReportId !== null,
    );
    const lockedExtension = getFileExtension(fileName);
    const displayBasename = stripFileExtension(fileName);
    const [editedBasename, setEditedBasename] = useState(displayBasename);
    const { t } = useTranslation(I18nNamespace.SOURCES);

    React.useEffect(() => {
        setEditedBasename(displayBasename);
    }, [displayBasename]);

    const commitEditedBasename = () => {
        const trimmedBasename = editedBasename.trim();
        if (trimmedBasename) {
            onFileNameChange(`${trimmedBasename}${lockedExtension}`);
        } else {
            setEditedBasename(displayBasename);
        }
    };

    const handleNameKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            commitEditedBasename();
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <TextField
                label={t('addModal.nameLabel')}
                required
                type="text"
                value={editedBasename}
                onChange={(event) => setEditedBasename(event.target.value)}
                onBlur={commitEditedBasename}
                onKeyDown={handleNameKeyDown}
            />

            {!isProcedure && (
                <AnalysisSettings
                    faceRecognition={faceRecognition}
                    onFaceRecognitionChange={onFaceRecognitionChange}
                    licensePlateRecognition={licensePlateRecognition}
                    onLicensePlateRecognitionChange={
                        onLicensePlateRecognitionChange
                    }
                    opticalCharacterRecognition={opticalCharacterRecognition}
                    onOpticalCharacterRecognitionChange={
                        onOpticalCharacterRecognitionChange
                    }
                    analysisFramesPerSecond={analysisFramesPerSecond}
                    onAnalysisFramesPerSecondChange={
                        onAnalysisFramesPerSecondChange
                    }
                />
            )}
        </div>
    );
}
